import { Component, Input, input, ChangeDetectionStrategy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from '@progress/kendo-angular-charts';
import { UnknownRateCustomer, TopUnknownRateInfo } from 'src/app/core/services/analytics.service';
import { BaseComponent } from 'src/app/core/shared/common/base.component';

@Component({
  selector: 'app-unknown-tickets-chart',
  standalone: true,
  imports: [CommonModule, ChartModule],
  templateUrl: './unknown-tickets-chart.component.html',
  styleUrl: './unknown-tickets-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnknownTicketsChartComponent extends BaseComponent implements OnChanges {
  @Input() topTicketInfo: { [key: string]: TopUnknownRateInfo } = {};
  @Input() unknownRateData: Array<UnknownRateCustomer> = [];

  sortedUnknownRateData: Array<UnknownRateCustomer> = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unknownRateData']) {
      this.updateSortedData();
    }
  }

  private updateSortedData(): void {
    // Guard against null/undefined data
    if (!this.unknownRateData || !Array.isArray(this.unknownRateData)) {
      this.sortedUnknownRateData = [];
      return;
    }

    // Create sorted copy with error handling
    // Sort by absolute unknown ticket count instead of percentage
    // This matches backend sorting logic
    try {
      this.sortedUnknownRateData = [...this.unknownRateData]
        .filter((item) => item && typeof item.unknown_tickets === 'number')
        .sort((a, b) => (b.unknown_tickets || 0) - (a.unknown_tickets || 0));
    } catch (error) {
      console.warn('Error sorting unknown rate data:', error);
      this.sortedUnknownRateData = [...this.unknownRateData];
    }
  }

  getTicketCount(customerName: string): number {
    if (!customerName || !this.unknownRateData) {
      return 0;
    }

    const customer = this.unknownRateData.find((d) => d.customer === customerName);
    return customer?.total_tickets || 0;
  }

  getUnknownTicketCount(customerName: string): number {
    if (!customerName || !this.unknownRateData) {
      return 0;
    }

    const customer = this.unknownRateData.find((d) => d.customer === customerName);
    return customer?.unknown_tickets || 0;
  }

  getMaxValueAxis(): number {
    if (!this.sortedUnknownRateData || this.sortedUnknownRateData.length === 0) {
      return 100; // Default to 100% if no data
    }

    // Find the maximum percentage in the data
    const maxPercentage = Math.max(
      ...this.sortedUnknownRateData.map((item) => item.customer_unknown_rate_percent || 0)
    );

    // If max is 0 or very small, default to 10%
    if (maxPercentage <= 0) {
      return 10;
    }

    // Calculate a reasonable upper bound
    // For small values (≤10%), round up to nearest 5% with minimum of 10%
    // For larger values, round up to nearest 10% with maximum of 100%
    let upperBound: number;

    if (maxPercentage <= 10) {
      upperBound = Math.ceil(maxPercentage / 5) * 5;
      upperBound = Math.max(upperBound, 10); // Minimum 10%
    } else {
      upperBound = Math.ceil(maxPercentage / 10) * 10;
      upperBound = Math.min(upperBound, 100); // Maximum 100%
    }

    return upperBound;
  }
}
